import { getLookupAssetTypeIdName } from '../api'
import { getEntitiesByTimestampAndAssetId } from '../api/static-data'
import { useCallback, useEffect, useRef, useState } from 'react'
import { isArray } from 'lodash'
import { validatePipeExpression } from 'live/components/time/span-utils'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

type State = {
    rigName?: string
    rigId?: number
}

export const useStaticData = (
    dashboardContext: LiveDashboardContextValue | Record<string, never> = {}
): {
    data: WellState[]
    get: (ctx: LiveDashboardContextValue) => Promise<WellState[]>
} => {
    const [state, setState] = useState<State>({})
    const [finalData, setFinalData] = useState<WellState[]>([])
    const doneRef = useRef(false)

    const getDashboardCurrentFilter = useCallback(async (ctx: LiveDashboardContextValue): string => {
        const [[currentFilter]] = await Object.values(ctx.state.currentFilterValues)

        return currentFilter
    }, [])

    const getRigInfo = useCallback(async (ctx: LiveDashboardContextValue): Promise<SDRigInfo> => {
        const rigName = await getDashboardCurrentFilter(ctx)
        const [lookupAssetTypeIdName] = await getLookupAssetTypeIdName().then(r =>
            r.entries.filter(e => e.value === rigName)
        )

        return {
            rigName: lookupAssetTypeIdName.value,
            rigId: parseInt(lookupAssetTypeIdName.key.split('/')[1])
        }
    }, [])

    const mountWellState = useCallback(async (allEntities: SDEntities): Promise<WellState[]> => {
        const data: WellState[] = []

        for await (const well of allEntities.wells) {
            if (data.find(d => d && d['wellId'] === well.id)) {
                break
            }

            data.push({
                wellName: well.name,
                begin: 0,
                end: 0,
                wellId: well.id,
                interventions: allEntities.interventions
                    .sort((a, b) => (a?.start < b?.start ? -1 : 1))
                    .map(intervention =>
                        intervention.well_id === well.id
                            ? {
                                  name: intervention.name,
                                  begin: intervention?.start,
                                  end: intervention?.end ?? undefined,
                                  interventionId: intervention?.id.toString(),
                                  sections: allEntities.sections
                                      .sort((a, b) => (a?.start < b?.start ? -1 : 1))
                                      .map(s =>
                                          s.intervention_id === intervention.id
                                              ? {
                                                    name: s?.name,
                                                    type: s?.type,
                                                    od: s?.oD,
                                                    begin: s.start,
                                                    end: s?.end ?? undefined,
                                                    runs: allEntities.runs
                                                        .sort((a, b) => (a?.start < b?.start ? -1 : 1))
                                                        .filter(r => r.section_id === s.id)
                                                }
                                              : null
                                      )
                              }
                            : []
                    )
                    .filter(e => !isArray(e))
            })
        }

        return data
    }, [])

    const getAllEntitiesByTimestamp = useCallback((dashboardSpan, rigId): Promise<SDEntities> => {
        return validatePipeExpression(dashboardSpan)
            .then((response: { data: { start: number; end: number } }) => {
                const currentTimestamp = Date.now()

                if (response.data.start > currentTimestamp || response.data.end < response.data.start) {
                    globalNotification.error(i18n('Invalid Time Span'))
                    return { start: 0, end: 60000 }
                }

                return { start: response.data.start, end: response.data.end }
            })
            .then(timestamp =>
                getEntitiesByTimestampAndAssetId({ rigId, start: timestamp.start, end: timestamp.end }).then(res => res)
            )
    }, [])

    const get = useCallback(async (ctx: LiveDashboardContextValue): Promise<WellState[]> => {
        const info = await getRigInfo(ctx)
        const allEntities = await getAllEntitiesByTimestamp(dashboardContext.params.span, info.rigId)
        const wellState = await mountWellState(allEntities)

        setFinalData(wellState)

        doneRef.current = true

        return wellState
    }, [])

    useEffect(() => {
        if (dashboardContext && dashboardContext?.state && !doneRef.current) {
            get(dashboardContext as LiveDashboardContextValue)
        }
    }, [])

    return { data: finalData, get }
}
